import React, { useId, useLayoutEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

// Badge color by project state (language-independent, set in content.js).
const badgeStyles = {
    completed: 'text-green-700 dark:text-green-400 bg-green-50 dark:bg-green-900/20',
    production: 'text-green-700 dark:text-green-400 bg-green-50 dark:bg-green-900/20',
    live: 'text-green-700 dark:text-green-400 bg-green-50 dark:bg-green-900/20',
    development: 'text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20',
};
const neutralBadge = 'text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-gray-700';

export const badgeClassFor = (state) => badgeStyles[state] || neutralBadge;

// A card is only a link when it has somewhere to go. `to` = internal route,
// `href` = external URL. The title carries the link and stretches over the
// whole card (after:inset-0), so the "Show more" button can sit on top of it
// without nesting interactive elements.
const ProjectCard = ({
    title,
    subtitle,
    desc,
    tech = [],
    status,
    state,
    icon,
    color,
    href,
    to,
    actionLabel,
    moreLabel,
    lessLabel,
}) => {
    const descId = useId();
    const descRef = useRef(null);
    const [expanded, setExpanded] = useState(false);
    const [overflowing, setOverflowing] = useState(false);
    const clickable = Boolean(href || to);

    // Only offer "Show more" when the 3-line clamp actually hides text.
    useLayoutEffect(() => {
        const el = descRef.current;
        if (!el || expanded) return undefined;
        const measure = () => setOverflowing(el.scrollHeight > el.clientHeight + 1);
        measure();
        if (typeof ResizeObserver === 'undefined') return undefined;
        const observer = new ResizeObserver(measure);
        observer.observe(el);
        return () => observer.disconnect();
    }, [desc, expanded]);

    const linkClass =
        "after:absolute after:inset-0 after:content-[''] focus:outline-none group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors";

    let titleNode = title;
    if (to) {
        titleNode = <Link to={to} className={linkClass}>{title}</Link>;
    } else if (href) {
        titleNode = (
            <a href={href} target="_blank" rel="noopener noreferrer" className={linkClass}>
                {title}
            </a>
        );
    }

    return (
        <article
            className={`reveal-card group relative bg-white dark:bg-gray-800 rounded-xl overflow-hidden shadow-lg flex flex-col h-full transition-all duration-300 ${clickable
                ? 'hover:shadow-2xl hover:-translate-y-2 focus-within:ring-2 focus-within:ring-blue-500'
                : ''
                }`}
        >
            <div className={`h-48 bg-gradient-to-br ${color} flex items-center justify-center text-white`}>
                <i
                    className={`${icon} text-6xl transform transition-transform duration-300 ${clickable ? 'group-hover:scale-110' : ''}`}
                    aria-hidden="true"
                ></i>
            </div>

            <div className="p-6 flex-grow flex flex-col">
                <div className="mb-4">
                    <h3 className="text-xl font-bold text-gray-800 dark:text-white mb-1">
                        {titleNode}
                    </h3>
                    <p className="text-sm text-blue-500 dark:text-blue-400 font-medium mb-2">
                        {subtitle}
                    </p>
                    <p
                        id={descId}
                        ref={descRef}
                        className={`text-gray-600 dark:text-gray-300 text-sm ${expanded ? '' : 'line-clamp-3'}`}
                    >
                        {desc}
                    </p>
                    {overflowing && (
                        <button
                            type="button"
                            onClick={() => setExpanded((open) => !open)}
                            aria-expanded={expanded}
                            aria-controls={descId}
                            className="relative z-10 mt-2 text-sm font-medium text-blue-600 dark:text-blue-400 hover:underline"
                        >
                            {expanded ? lessLabel : moreLabel}
                        </button>
                    )}
                </div>

                <div className="mt-auto">
                    <div className="flex flex-wrap gap-2 mb-4">
                        {tech.map((item) => (
                            <span key={item} className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 text-xs rounded-md">
                                {item}
                            </span>
                        ))}
                    </div>
                    <div className="flex justify-between items-center gap-3 pt-4 border-t border-gray-100 dark:border-gray-700">
                        <span className={`text-xs font-semibold px-2 py-1 rounded ${badgeClassFor(state)}`}>
                            {status}
                        </span>
                        {clickable && (
                            <span
                                aria-hidden="true"
                                className="text-blue-600 dark:text-blue-400 text-sm font-medium group-hover:translate-x-1 transition-transform flex items-center gap-1"
                            >
                                {actionLabel} <i className="fas fa-arrow-right"></i>
                            </span>
                        )}
                    </div>
                </div>
            </div>
        </article>
    );
};

export default ProjectCard;
